import { FileSpreadsheet, FileUp, PencilLine } from 'lucide-react';

import { Chip } from '@/components/ui/Chip';
import { Modal } from '@/components/ui/Modal';

import styles from './AddCandidateChooser.module.css';

/**
 * How a candidate gets into the register.
 *
 * Three routes, one of which does not exist yet. Reading the details out of an
 * uploaded CV is a later version, and this is the only place in the portal that says
 * so. Without it, the absence is just an absence and somebody wonders whether they
 * missed a button.
 *
 * The unbuilt route is not a disabled button. A disabled button cannot be focused and
 * is skipped by a screen reader, which would hide the explanation from exactly the
 * people most likely to go looking for it. There is nothing to activate, so it is not
 * a control.
 */
export function AddCandidateChooser({
  open,
  onClose,
  onManual,
  onImport,
}: {
  open: boolean;
  onClose: () => void;
  onManual: () => void;
  onImport: () => void;
}) {
  return (
    <Modal open={open} onClose={onClose} title="Add a candidate" size="md">
      <div className={styles.routes}>
        <button type="button" className={styles.route} onClick={onManual}>
          <span className={styles.icon}>
            <PencilLine size={18} />
          </span>
          <span className={styles.text}>
            <span className={styles.name}>Enter the details</span>
            <span className={styles.body}>
              Fill in the form. You can attach a CV and a passport as you go.
            </span>
          </span>
        </button>

        <button type="button" className={styles.route} onClick={onImport}>
          <span className={styles.icon}>
            <FileSpreadsheet size={18} />
          </span>
          <span className={styles.text}>
            <span className={styles.name}>Import a spreadsheet</span>
            <span className={styles.body}>
              Upload a CSV and every valid row becomes a candidate. You get to see what it will
              do before anything is created.
            </span>
          </span>
        </button>

        <div className={styles.soon}>
          <span className={styles.icon}>
            <FileUp size={18} />
          </span>
          <span className={styles.text}>
            <span className={styles.name}>
              Upload a CV
              <Chip size="sm" tone="applicant-arranged">
                Coming soon
              </Chip>
            </span>
            <span className={styles.body}>
              Reading the details straight out of a CV arrives in a later version. Until then,
              attach the CV on the form and type the rest.
            </span>
          </span>
        </div>
      </div>
    </Modal>
  );
}
